import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTenant } from "../lib/tenant-context";

export const Route = createFileRoute("/warehouse/")({
  component: WarehousePackageList,
});

// Brand colors per tenant
const TENANT_COLORS: Record<string, { primary: string; secondary: string }> = {
  nespresso: { primary: "#6B4423", secondary: "#C5A572" },
  adidas: { primary: "#000000", secondary: "#666666" },
};

type PackageStage = "received" | "processing" | "processed";

interface WarehousePackage {
  id: string;
  bagLabelId: string;
  weight: number;
  estimatedPodCount: number;
  actualPodCount: number | null;
  stage: PackageStage;
  customerName: string;
  customerEmail: string;
  customerTier: string;
  kioskLocation: string;
  kioskCity: string;
  receivedAt: string;
  processingStartedAt: string | null;
  processedAt: string | null;
  hasFlag: boolean;
  flagNote: string | null;
}

// Adidas demo data — takeback apparel & footwear packages
const ADIDAS_PACKAGES: WarehousePackage[] = [
  {
    id: "adi_pkg_001",
    bagLabelId: "ADI-2026-004501",
    weight: 3.8,
    estimatedPodCount: 6,
    actualPodCount: null,
    stage: "received",
    customerName: "Sarah Chen",
    customerEmail: "s.chen@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Oxford Street",
    kioskCity: "London",
    receivedAt: "2026-02-18T09:30:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_002",
    bagLabelId: "ADI-2026-004502",
    weight: 2.1,
    estimatedPodCount: 3,
    actualPodCount: null,
    stage: "received",
    customerName: "Marcus Johnson",
    customerEmail: "m.johnson@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Brooklyn",
    kioskCity: "New York",
    receivedAt: "2026-02-18T11:15:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_003",
    bagLabelId: "ADI-2026-004498",
    weight: 5.4,
    estimatedPodCount: 8,
    actualPodCount: null,
    stage: "received",
    customerName: "Emma Lindqvist",
    customerEmail: "e.lindqvist@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Herzogenaurach",
    kioskCity: "Herzogenaurach",
    receivedAt: "2026-02-17T14:00:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_004",
    bagLabelId: "ADI-2026-004495",
    weight: 1.9,
    estimatedPodCount: 2,
    actualPodCount: null,
    stage: "processing",
    customerName: "Kenji Tanaka",
    customerEmail: "k.tanaka@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Brand Center Tokyo",
    kioskCity: "Tokyo",
    receivedAt: "2026-02-16T10:20:00Z",
    processingStartedAt: "2026-02-17T08:00:00Z",
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_005",
    bagLabelId: "ADI-2026-004490",
    weight: 4.2,
    estimatedPodCount: 5,
    actualPodCount: null,
    stage: "processing",
    customerName: "Léa Fontaine",
    customerEmail: "l.fontaine@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Champs-Élysées",
    kioskCity: "Paris",
    receivedAt: "2026-02-15T16:45:00Z",
    processingStartedAt: "2026-02-16T09:30:00Z",
    processedAt: null,
    hasFlag: true,
    flagNote: "Contains non-adidas items mixed in",
  },
  {
    id: "adi_pkg_006",
    bagLabelId: "ADI-2026-004482",
    weight: 3.0,
    estimatedPodCount: 4,
    actualPodCount: 4,
    stage: "processed",
    customerName: "Alex Rodriguez",
    customerEmail: "a.rodriguez@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Santa Monica",
    kioskCity: "Los Angeles",
    receivedAt: "2026-02-14T12:00:00Z",
    processingStartedAt: "2026-02-15T08:00:00Z",
    processedAt: "2026-02-15T10:30:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_007",
    bagLabelId: "ADI-2026-004478",
    weight: 6.1,
    estimatedPodCount: 10,
    actualPodCount: 9,
    stage: "processed",
    customerName: "Priya Sharma",
    customerEmail: "p.sharma@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Flagship Mumbai",
    kioskCity: "Mumbai",
    receivedAt: "2026-02-13T09:30:00Z",
    processingStartedAt: "2026-02-14T08:00:00Z",
    processedAt: "2026-02-14T11:45:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "adi_pkg_008",
    bagLabelId: "ADI-2026-004470",
    weight: 2.5,
    estimatedPodCount: 3,
    actualPodCount: 3,
    stage: "processed",
    customerName: "Jonas Weber",
    customerEmail: "j.weber@email.com",
    customerTier: "Creators Club",
    kioskLocation: "adidas Store Berlin Mitte",
    kioskCity: "Berlin",
    receivedAt: "2026-02-12T15:20:00Z",
    processingStartedAt: "2026-02-13T09:00:00Z",
    processedAt: "2026-02-13T11:00:00Z",
    hasFlag: false,
    flagNote: null,
  },
];

// Nespresso demo data for the warehouse package list
const NESPRESSO_PACKAGES: WarehousePackage[] = [
  {
    id: "pkg_001",
    bagLabelId: "NES-2025-001234",
    weight: 2.4,
    estimatedPodCount: 48,
    actualPodCount: null,
    stage: "received",
    customerName: "Marie Laurent",
    customerEmail: "marie.laurent@email.com",
    customerTier: "Gold",
    kioskLocation: "Nespresso Boutique Champs-Élysées",
    kioskCity: "Paris",
    receivedAt: "2025-01-22T08:30:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_002",
    bagLabelId: "NES-2025-001235",
    weight: 1.8,
    estimatedPodCount: 36,
    actualPodCount: null,
    stage: "received",
    customerName: "Jean-Pierre Dubois",
    customerEmail: "jp.dubois@email.com",
    customerTier: "Platinum",
    kioskLocation: "Nespresso Boutique Le Marais",
    kioskCity: "Paris",
    receivedAt: "2025-01-22T09:15:00Z",
    processingStartedAt: null,
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_003",
    bagLabelId: "NES-2025-001230",
    weight: 3.2,
    estimatedPodCount: 64,
    actualPodCount: null,
    stage: "processing",
    customerName: "Sophie Martin",
    customerEmail: "sophie.m@email.com",
    customerTier: "Gold",
    kioskLocation: "Nespresso Boutique Lyon",
    kioskCity: "Lyon",
    receivedAt: "2025-01-21T14:20:00Z",
    processingStartedAt: "2025-01-22T10:00:00Z",
    processedAt: null,
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_004",
    bagLabelId: "NES-2025-001228",
    weight: 2.1,
    estimatedPodCount: 42,
    actualPodCount: null,
    stage: "processing",
    customerName: "François Petit",
    customerEmail: "f.petit@email.com",
    customerTier: "Silver",
    kioskLocation: "Nespresso Boutique Marseille",
    kioskCity: "Marseille",
    receivedAt: "2025-01-21T11:45:00Z",
    processingStartedAt: "2025-01-22T09:30:00Z",
    processedAt: null,
    hasFlag: true,
    flagNote: "Contains some non-Nespresso capsules",
  },
  {
    id: "pkg_005",
    bagLabelId: "NES-2025-001220",
    weight: 2.8,
    estimatedPodCount: 56,
    actualPodCount: 52,
    stage: "processed",
    customerName: "Claire Moreau",
    customerEmail: "claire.moreau@email.com",
    customerTier: "Platinum",
    kioskLocation: "Nespresso Boutique Bordeaux",
    kioskCity: "Bordeaux",
    receivedAt: "2025-01-20T16:00:00Z",
    processingStartedAt: "2025-01-21T08:00:00Z",
    processedAt: "2025-01-21T08:45:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_006",
    bagLabelId: "NES-2025-001218",
    weight: 1.5,
    estimatedPodCount: 30,
    actualPodCount: 28,
    stage: "processed",
    customerName: "Pierre Blanc",
    customerEmail: "p.blanc@email.com",
    customerTier: "Gold",
    kioskLocation: "Nespresso Boutique Nice",
    kioskCity: "Nice",
    receivedAt: "2025-01-20T10:30:00Z",
    processingStartedAt: "2025-01-21T09:00:00Z",
    processedAt: "2025-01-21T09:30:00Z",
    hasFlag: false,
    flagNote: null,
  },
  {
    id: "pkg_007",
    bagLabelId: "NES-2025-001215",
    weight: 4.0,
    estimatedPodCount: 80,
    actualPodCount: 95,
    stage: "processed",
    customerName: "Isabelle Rousseau",
    customerEmail: "i.rousseau@email.com",
    customerTier: "Platinum",
    kioskLocation: "Nespresso Boutique Toulouse",
    kioskCity: "Toulouse",
    receivedAt: "2025-01-19T13:00:00Z",
    processingStartedAt: "2025-01-20T08:00:00Z",
    processedAt: "2025-01-20T09:15:00Z",
    hasFlag: false,
    flagNote: null,
  },
];

// Select packages by tenant
const TENANT_PACKAGES: Record<string, WarehousePackage[]> = {
  nespresso: NESPRESSO_PACKAGES,
  adidas: ADIDAS_PACKAGES,
};

function WarehousePackageList() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [activeStage, setActiveStage] = useState<PackageStage | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");

  const packages =
    TENANT_PACKAGES[tenantId || "nespresso"] || NESPRESSO_PACKAGES;
  const colors =
    TENANT_COLORS[tenantId || "nespresso"] || TENANT_COLORS.nespresso;

  const handlePackageClick = (pkg: WarehousePackage) => {
    void navigate({ to: `/warehouse/package/${pkg.id}` as string });
  };

  // Filter packages by stage and search query
  const filteredPackages = packages.filter((pkg) => {
    const matchesStage = activeStage === "all" || pkg.stage === activeStage;
    const matchesSearch =
      searchQuery === "" ||
      pkg.bagLabelId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pkg.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStage && matchesSearch;
  });

  // Count packages by stage
  const stageCounts = {
    received: packages.filter((p) => p.stage === "received").length,
    processing: packages.filter((p) => p.stage === "processing").length,
    processed: packages.filter((p) => p.stage === "processed").length,
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeInStage = (pkg: WarehousePackage) => {
    const now = new Date();
    let stageStart: Date;

    if (pkg.stage === "received") {
      stageStart = new Date(pkg.receivedAt);
    } else if (pkg.stage === "processing" && pkg.processingStartedAt) {
      stageStart = new Date(pkg.processingStartedAt);
    } else {
      return null;
    }

    const hours = Math.floor(
      (now.getTime() - stageStart.getTime()) / (1000 * 60 * 60),
    );
    if (hours < 24) {
      return `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const getStageColor = (stage: PackageStage) => {
    switch (stage) {
      case "received":
        return { bg: "#FEF3C7", text: "#92400E" };
      case "processing":
        return { bg: "#DBEAFE", text: "#1E40AF" };
      case "processed":
        return { bg: "#D1FAE5", text: "#065F46" };
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: "#171717" }}>
          Warehouse Packages
        </h1>
        <p className="text-sm mt-1" style={{ color: "#A9A9A9" }}>
          Track and manage takeback packages through the warehouse workflow
        </p>
      </div>

      {/* Stage Tabs and Search */}
      <div className="flex items-center justify-between mb-6">
        {/* Stage Filter Tabs */}
        <div className="flex gap-2">
          <StageTab
            label="All"
            count={packages.length}
            isActive={activeStage === "all"}
            onClick={() => setActiveStage("all")}
          />
          <StageTab
            label="Received"
            count={stageCounts.received}
            isActive={activeStage === "received"}
            onClick={() => setActiveStage("received")}
            color="#92400E"
          />
          <StageTab
            label="Processing"
            count={stageCounts.processing}
            isActive={activeStage === "processing"}
            onClick={() => setActiveStage("processing")}
            color="#1E40AF"
          />
          <StageTab
            label="Processed"
            count={stageCounts.processed}
            isActive={activeStage === "processed"}
            onClick={() => setActiveStage("processed")}
            color="#065F46"
          />
        </div>

        {/* Search */}
        <div className="relative">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by label ID or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2 border rounded-lg text-sm w-64 focus:outline-none focus:ring-2"
            style={{
              borderColor: "rgba(169,169,169,0.3)",
              backgroundColor: "#FFFFFF",
            }}
          />
        </div>
      </div>

      {/* Package List */}
      <div
        className="bg-white rounded-lg border"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        {/* Table Header */}
        <div
          className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-medium border-b"
          style={{ color: "#A9A9A9", borderColor: "rgba(169,169,169,0.2)" }}
        >
          <div className="col-span-2">Bag Label</div>
          <div className="col-span-2">Customer</div>
          <div className="col-span-2">
            {tenantId === "nespresso" ? "Kiosk Location" : "Drop-off Location"}
          </div>
          <div className="col-span-1">Weight</div>
          <div className="col-span-1">
            {tenantId === "nespresso" ? "Est. Pods" : "Est. Items"}
          </div>
          <div className="col-span-1">Stage</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-1">Time</div>
        </div>

        {/* Package Rows */}
        {filteredPackages.length === 0 ? (
          <div className="px-4 py-12 text-center" style={{ color: "#A9A9A9" }}>
            No packages found matching your criteria
          </div>
        ) : (
          filteredPackages.map((pkg) => {
            const stageColors = getStageColor(pkg.stage);
            const timeInStage = getTimeInStage(pkg);

            return (
              <div
                key={pkg.id}
                className="grid grid-cols-12 gap-4 px-4 py-3 border-b items-center hover:bg-gray-50 transition-colors cursor-pointer"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
                onClick={() => handlePackageClick(pkg)}
              >
                <div className="col-span-2">
                  <div className="flex items-center gap-2">
                    {pkg.hasFlag && <AlertIcon />}
                    <span
                      className="font-mono text-sm"
                      style={{ color: "#171717" }}
                    >
                      {pkg.bagLabelId}
                    </span>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white"
                      style={{ backgroundColor: colors.primary }}
                    >
                      {pkg.customerName.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: "#171717" }}>
                        {pkg.customerName}
                      </p>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        {pkg.customerTier}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <p className="text-sm" style={{ color: "#171717" }}>
                    {pkg.kioskCity}
                  </p>
                  <p className="text-xs truncate" style={{ color: "#A9A9A9" }}>
                    {pkg.kioskLocation}
                  </p>
                </div>
                <div
                  className="col-span-1 text-sm"
                  style={{ color: "#171717" }}
                >
                  {pkg.weight} kg
                </div>
                <div
                  className="col-span-1 text-sm"
                  style={{ color: "#171717" }}
                >
                  {pkg.stage === "processed" && pkg.actualPodCount !== null ? (
                    <span>
                      {pkg.actualPodCount}
                      {Math.abs(pkg.actualPodCount - pkg.estimatedPodCount) >
                        pkg.estimatedPodCount * 0.2 && (
                        <span className="text-xs ml-1 text-amber-600">
                          (est. {pkg.estimatedPodCount})
                        </span>
                      )}
                    </span>
                  ) : (
                    `~${pkg.estimatedPodCount}`
                  )}
                </div>
                <div className="col-span-1">
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium capitalize"
                    style={{
                      backgroundColor: stageColors.bg,
                      color: stageColors.text,
                    }}
                  >
                    {pkg.stage}
                  </span>
                </div>
                <div
                  className="col-span-2 text-sm"
                  style={{ color: "#A9A9A9" }}
                >
                  {formatDate(pkg.receivedAt)}
                </div>
                <div
                  className="col-span-1 text-sm"
                  style={{ color: "#A9A9A9" }}
                >
                  {timeInStage || "-"}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Summary Footer */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm" style={{ color: "#A9A9A9" }}>
          Showing {filteredPackages.length} of {packages.length} packages
        </p>
      </div>
    </div>
  );
}

function StageTab({
  label,
  count,
  isActive,
  onClick,
  color,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
  color?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
      style={
        isActive
          ? {
              backgroundColor: color ? `${color}15` : "#17171710",
              color: color || "#171717",
            }
          : {
              backgroundColor: "transparent",
              color: "#A9A9A9",
            }
      }
    >
      {label}
      <span
        className="px-1.5 py-0.5 rounded text-xs"
        style={{
          backgroundColor: isActive ? color || "#171717" : "#E5E5E5",
          color: isActive ? "#FFFFFF" : "#A9A9A9",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#A9A9A9"
      strokeWidth="1.5"
      className="absolute left-3 top-1/2 -translate-y-1/2"
    >
      <circle cx="11" cy="11" r="8" />
      <path
        d="M21 21L16.65 16.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#F59E0B"
      strokeWidth="2"
    >
      <path
        d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 9V13" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 17H12.01" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
